/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { ExcelGeneratorData } from 'src/app/dto/excel-generation-data.dto';
import { Order } from 'src/app/dto/order.dto';
import { RtsVehicle } from 'src/app/dto/get-rts-result.dto';
import { XlsxTemplateFillerServiceService } from './xlsx-template-filler-service.service';
import { DateTimeService } from './date-time.service';
import { TRN_TEMPLATE_TPL_PATH } from '../../utils/constants/document-template.constants';
import { DocumentFieldFormatter } from '../../utils/document-field-formatter.util';

@Injectable({
  providedIn: 'root'
})
export class TrnGeneratorService {
  constructor(
    private xlsxTemplateFillerServiceService: XlsxTemplateFillerServiceService,
    private dateTimeService: DateTimeService
  ) {}

  async getTrn(order: Order, carInfo: RtsVehicle, currentDayId: string): Promise<void> {
    const data = await this.generateTrnDataStructure(order, carInfo, currentDayId);
    await this.xlsxTemplateFillerServiceService.generateAndOpenNewExcelFile(
      TRN_TEMPLATE_TPL_PATH,
      data,
      `ТРН-${order.id}`
    );
  }

  public async generateTrnDataStructure(order: Order, carInfo: RtsVehicle, currentDayId: string) {
    const generateTrnData: ExcelGeneratorData = {
      cellDataArray: []
    };

    const day = order?.getDayById?.(currentDayId);
    const shipmentDateStr =
      (day?.shipmentDate ? day.shipmentDate.split(' ')[0] : null) ??
      this.dateTimeService.getNowDateStringFormat();

    generateTrnData.cellDataArray.push({
      address: 'G9',
      value: shipmentDateStr,
      name: 'date',
      pageNumber: 1
    });

    generateTrnData.cellDataArray.push({
      address: 'AC9',
      value: `${carInfo.loadingData.ttnId}`,
      name: 'trn-number',
      pageNumber: 1
    });

    generateTrnData.cellDataArray.push({
      address: 'BU9',
      value: shipmentDateStr,
      name: 'date-create',
      pageNumber: 1
    });

    generateTrnData.cellDataArray.push({
      address: 'CQ9:DG9',
      value: DocumentFieldFormatter.safeValue(order.contractName),
      name: 'order-number',
      pageNumber: 1
    });

    if (order.forwarder) {
      const forwarderData = DocumentFieldFormatter.safeConcatenate([
        { value: order.forwarder.orgName },
        { value: order.forwarder.inn },
        { value: order.forwarder.address },
        { value: order.forwarder.phone, prefix: 'тел. ' },
        { value: order.forwarder.kpp, prefix: 'КПП. ' }
      ], ' ');

      generateTrnData.cellDataArray.push({
        address: 'B15',
        value: forwarderData,
        name: 'expeditor-data',
        pageNumber: 1
      });

      generateTrnData.cellDataArray.push({
        address: 'A56',
        value: forwarderData,
        name: 'expeditor-data',
        pageNumber: 1
      });
    }

    if (order.buyer) {
      const buyerData = DocumentFieldFormatter.safeConcatenate([
        { value: order.buyer?.orgName },
        { value: order.buyer?.inn },
        { value: order.buyer?.address },
        { value: order.buyer?.phone, prefix: 'тел. ' },
        { value: order.buyer?.kpp, prefix: 'КПП. ' }
      ], ' ');

      generateTrnData.cellDataArray.push({
        address: 'BP15',
        value: buyerData,
        name: 'buyer-data',
        pageNumber: 1
      });
    }

    if (order.consignee) {
      const consigneeData = DocumentFieldFormatter.safeConcatenate([
        { value: order.consignee.name },
        { value: order.consignee.inn },
        { value: order.consignee.kpp, prefix: 'КПП. ' },
        { value: order.consignee.ogrn, prefix: 'ОГРН. ' },
        { value: order.consignee.address }

      ], ' ');

      generateTrnData.cellDataArray.push({
        address: 'B20',
        value: consigneeData,
        name: 'consignee-data',
        pageNumber: 1
      });
    }

    generateTrnData.cellDataArray.push({
      address: 'BP17',
      value: `Договор ТЭО № ________ от __.__.____ , Поручение экспедитору №${order.contractName} от ${this.dateTimeService.getDateFromTimestamp(order.createdDate)}`,
      name: 'order-data',
      pageNumber: 1
    });

    if (order.unloading?.address) {
      generateTrnData.cellDataArray.push({
        address: 'B22',
        value: DocumentFieldFormatter.safeValue(order.unloading.address, ' '),
        name: 'unloading-address',
        pageNumber: 1
      });
    }

    if (order.freightName) {
      const freightData = DocumentFieldFormatter.trnFreight(order.freightName, order.cropYear);

      generateTrnData.cellDataArray.push({
        address: 'B25',
        value: freightData,
        name: 'freight-data',
        pageNumber: 1
      });
    }

    generateTrnData.cellDataArray.push({
      address: 'B27',
      value: `Масса (брутто) ${carInfo.loadingData.grossWeight.toLocaleString('ru-RU')}, тара  ${carInfo.loadingData.tareWeight.toLocaleString('ru-RU')}, масса (нетто)  ${carInfo.loadingData.netWeight.toLocaleString('ru-RU')}`,
      name: 'weight-data',
      pageNumber: 1
    });

    const additionalDocsData = DocumentFieldFormatter.safeConcatenate([
      {
        value: carInfo.loadingData.declarationOfConformityNumber,
        prefix: 'Декларация соответствия ',
        suffix: ` от ${this.dateTimeService.getDateFromTimestamp(carInfo.loadingData.declarationOfConformityDate)}`
      },
      {
        value: carInfo.loadingData.quarantineCertificateNumber,
        prefix: 'Карантинный сертификат ',
        suffix: ` от ${this.dateTimeService.getDateFromTimestamp(carInfo.loadingData.quarantineCertificateDate)}`
      },
      {
        value: carInfo.loadingData.sdizNumber,
        prefix: 'СДИЗ '
      }
    ], ', ');

    generateTrnData.cellDataArray.push({
      address: 'B34',
      value: additionalDocsData,
      name: 'additional-docs',
      pageNumber: 1
    });

    const ttnInfo = `ТТН (по форме СП-31) ${carInfo.loadingData.ttnId}
    от ${this.dateTimeService.getNowDateStringFormat()}, ИНН составителя - ${DocumentFieldFormatter.safeValue(order.forwarder.inn)}`;

    generateTrnData.cellDataArray.push({
      address: 'B36',
      value: ttnInfo,
      name: 'trn-info',
      pageNumber: 1
    });

    if (order.forwarder) {
      const managerData = DocumentFieldFormatter.safeConcatenate([
        { value: order.forwarder.orgName },
        { value: order.forwarder.phone }
      ], ' ');

      generateTrnData.cellDataArray.push({
        address: 'BF39',
        value: managerData,
        name: 'manager-data',
        pageNumber: 1
      });
    }

    if (carInfo) {
      const carrierData = DocumentFieldFormatter.safeConcatenate([
        { value: carInfo.carrierName },
        { value: carInfo.carrierInn },
        { value: carInfo.carrierAddress },
        { value: carInfo.carrierEmail, prefix: ', email :' }
      ], ' ');

      generateTrnData.cellDataArray.push({
        address: 'B44',
        value: carrierData,
        name: 'carrier-data',
        pageNumber: 1
      });
    }

    if (carInfo) {
      const driverData = DocumentFieldFormatter.safeConcatenate([
        { value: carInfo.driverName },
        { value: carInfo.driverLicenseNumber, prefix: ', ВУ ' },
        { value: carInfo.driverPhone, prefix: ', тел.:' }
      ], '');

      generateTrnData.cellDataArray.push({
        address: 'BF44',
        value: driverData,
        name: 'driver-data',
        pageNumber: 1
      });

      const carNumberData = DocumentFieldFormatter.safeConcatenate([
        { value: carInfo.registrationNumber, suffix: ' rus' },
        { value: carInfo.trailerRegistrationNumber, prefix: ', прицеп ', suffix: ' rus' }
      ], '');

      generateTrnData.cellDataArray.push({
        address: 'BF47',
        value: carNumberData,
        name: 'car-number',
        pageNumber: 1
      });
    }

    if (order.seller) {
      const ownerInfo = DocumentFieldFormatter.safeConcatenate([
        { value: order.seller.orgName },
        { value: order.seller.inn, prefix: '(ИНН ', suffix: ')' }
      ], ' ');



      const fullInfo = DocumentFieldFormatter.safeConcatenate([
        { value: ownerInfo },
        { value: order.loading.address }
      ], ' ');

      generateTrnData.cellDataArray.push({
        address: 'B58',
        value: fullInfo,
        name: 'loader-info',
        pageNumber: 1
      });
    }

    if (order.loading?.address) {
      generateTrnData.cellDataArray.push({
        address: 'B60',
        value: DocumentFieldFormatter.safeValue(order.loading.address),
        name: 'loading-address',
        pageNumber: 1
      });
    }

    generateTrnData.cellDataArray.push({
      address: 'BF60',
      value: '',
      name: 'loading-start-time',
      pageNumber: 1
    });

    generateTrnData.cellDataArray.push({
      address: 'B64',
      value: `Масса (брутто) ${carInfo.loadingData.grossWeight.toLocaleString('ru-RU')}, тара  ${carInfo.loadingData.tareWeight.toLocaleString('ru-RU')}, масса (нетто)  ${carInfo.loadingData.netWeight.toLocaleString('ru-RU')} (определение разницы между массой транспортного средства после погрузки и перед погрузкой по общей массе)`,
      name: 'loading-start-time',
      pageNumber: 1
    });

    if (order.forwarder?.orgName) {
      generateTrnData.cellDataArray.push({
        address: 'J70',
        value: DocumentFieldFormatter.safeValue(order.forwarder.orgName),
        name: 'forwarder-sign',
        pageNumber: 1
      });
    }

    if (carInfo.driverName) {
      generateTrnData.cellDataArray.push({
        address: 'BR70',
        value: `${carInfo.driverName}`,
        name: 'driver-sign',
        pageNumber: 1
      });
    }

    if (order.unloading?.address) {
      const unloadingData = DocumentFieldFormatter.safeConcatenate([
        { value: order.unloading.title },
        { value: order.unloading.address }
      ], ', ');

      generateTrnData.cellDataArray.push({
        address: 'B3',
        value: unloadingData,
        name: 'unload-address',
        pageNumber: 2
      });
    }

    generateTrnData.cellDataArray.push({
      address: 'BF3',
      value: '',
      name: 'unload-end-date',
      pageNumber: 2
    });

    if (order.forwarder?.orgName) {
      generateTrnData.cellDataArray.push({
        address: 'BR11',
        value: DocumentFieldFormatter.safeValue(order.forwarder.orgName),
        name: 'expeditor-name',
        pageNumber: 2
      });
    }

    order.generateTrnActions(generateTrnData.cellDataArray);

    return generateTrnData;
  }
}
